import { noop } from "lodash";
import type { LucideProps } from "lucide-react";
import { Check, ChevronsUpDown, CirclePlus, X } from "lucide-react";
import type { ForwardRefExoticComponent, RefAttributes } from "react";
import * as React from "react";
import { useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";

import { Button, type ButtonProps } from "@/components/ui/button";
import { Shortcut } from "@/components/ui/shortcut";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ComboOption {
  label: string;
  subLabel?: string;
  alternativeLabels: string[];
  value: any;
}

type CreateNewConfig = {
  label: string;
  onCreateNew: (searchTerm: string) => void;
};

type Props = {
  currentOption?: ComboOption;

  onChange: (value: any) => void;
  placeholder?: string;
  emptyLabel:
    | string
    | React.ReactNode
    | ((props: { input: string }) => React.ReactNode);
  items: ComboOption[];
  variant?: any;
  popoverAlignStart?: boolean;
  className?: ButtonProps["className"];
  fullWidth?: boolean;
  onFocus?: () => void;
  side?: "top" | "bottom";
  sideOffset?: number;
  container?: React.RefObject<HTMLElement>;
  modal?: boolean;
  onOpenChange?: (value: boolean) => void;
  readOnly?: string | React.ReactNode | boolean;
  disabled?: string | React.ReactNode | boolean;
  specialNotice?: React.ReactNode;
  align?: "start" | "center" | "end";
  children?: (props: {
    value?: ComboOption;
    placeholder?: string;
    readOnly?: string | React.ReactNode | boolean;
    disabled?: string | React.ReactNode | boolean;
  }) => React.ReactNode;
  icon?: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
  enableClear?: boolean;
  highlightWhenSelected?: boolean;
  onBlur?: () => void;
  open?: boolean;
  createNew?: CreateNewConfig;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLButtonElement | HTMLInputElement>,
  ) => void;
};

const ComboBox = ({
  currentOption,
  onChange,
  placeholder,
  emptyLabel,
  items,
  disabled = undefined,
  readOnly = undefined,
  variant = "ghost",
  align = "start",
  className,
  fullWidth = true,
  onFocus = noop,
  side = "bottom",
  sideOffset,
  container,
  modal = true,
  onOpenChange = noop,
  specialNotice,
  children,
  enableClear = false,
  highlightWhenSelected = false,
  onBlur = noop,
  open: controlledOpen,
  createNew,
  onKeyDown = noop,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const [value, setValue] = React.useState(currentOption);
  const [inputValue, setInputValue] = React.useState("");
  const comboBoxId = React.useId();

  const open = controlledOpen ?? uncontrolledOpen;

  React.useEffect(() => {
    setValue(currentOption);
  }, [currentOption]);

  React.useEffect(() => {
    if (!open) return;

    const handleFocusChange = (e: FocusEvent) => {
      setTimeout(() => {
        const activeElement = document.activeElement;
        const buttonElement = ref.current;

        if (buttonElement && buttonElement.contains(activeElement)) {
          return;
        }

        const isInPopover = activeElement?.closest("[cmdk-root]");
        if (isInPopover) {
          return;
        }

        setUncontrolledOpen(false);
      }, 50);
    };

    document.addEventListener("focusin", handleFocusChange);
    return () => {
      document.removeEventListener("focusin", handleFocusChange);
    };
  }, [open]);

  const onSelect = (newValue: any) => {
    onChange(newValue);
    setUncontrolledOpen(false);
  };

  // CreateNewOption component for consistent styling
  const CreateNewOption = ({
    label,
    searchTerm,
    onCreateNew,
  }: {
    label: string;
    searchTerm: string;
    onCreateNew: (searchTerm: string) => void;
  }) => (
    <div
      onClick={() => {
        onCreateNew(searchTerm);
        setUncontrolledOpen(false);
      }}
      className="flex cursor-pointer items-center justify-center gap-2 p-2 text-sm bg-accent text-accent-foreground hover:bg-accent/80 transition-colors"
      role="option"
      aria-selected="false"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCreateNew(searchTerm);
          setUncontrolledOpen(false);
        }
      }}
    >
      <CirclePlus size={16} />
      <p>{label}: </p>
      <p className="block max-w-48 truncate font-semibold text-primary">
        "{searchTerm}"
      </p>
      <div className="ml-auto pl-1">
        <div className="flex items-center gap-1 bg-border rounded px-1.5 py-1">
          <span className="text-[10px] capitalize leading-none">
            {t("enter")}
          </span>
          <Shortcut.ShortcutEnter size="sm" className="bg-transparent p-0" />
        </div>
      </div>
    </div>
  );

  const handlePopoverOpen = (value: boolean) => {
    setUncontrolledOpen(value);
    onOpenChange(value);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setValue(undefined);
    onChange(null);
  };

  const filterBySearch = (value: string, _search: string) => {
    const search = _search.replace(/ /g, "");
    const valueOptions = items.find((item) => item.value === value);
    const valueLabel = valueOptions?.label.replace(/ /g, "");
    const valueSubLabel =
      valueOptions?.subLabel?.replace(/ /g, "") ?? undefined;

    const labelIncludes = valueLabel
      ?.toLocaleLowerCase()
      .includes(search.toLocaleLowerCase())
      ? 1
      : 0;

    const subLabelIncludes = valueSubLabel
      ?.toLocaleLowerCase()
      .includes(search.toLocaleLowerCase())
      ? 1
      : 0;

    return Math.max(labelIncludes, subLabelIncludes);
  };

  // Calculate if there are any filtered results using the same logic as the Command component
  const hasFilteredResults = React.useMemo(() => {
    if (!inputValue.trim()) return items.length > 0;
    return items.some((item) => filterBySearch(item.value, inputValue) > 0);
  }, [items, inputValue]);

  // Handle Enter key when createNew is provided and no results found -> trigger createNew callback
  useHotkeys(
    "enter",
    (e) => {
      if (createNew && open && inputValue.trim() && !hasFilteredResults) {
        const activeElement = document.activeElement;
        const isCommandInput = activeElement?.hasAttribute("cmdk-input");

        if (isCommandInput) {
          e.preventDefault();
          e.stopPropagation();
          createNew.onCreateNew(inputValue);
          setUncontrolledOpen(false);
        }
      }
    },
    {
      enableOnContentEditable: true,
      enableOnFormTags: true,
    },
    [createNew, open, inputValue, hasFilteredResults],
  );

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const input = inputRef.current;

      if (!input) {
        return;
      }

      //@ts-ignore hackity hack to ignore keyboard enter and only use programmatic enter
      if (event.key === "Enter" && event.nativeEvent.isTrusted) {
        setTimeout(() => {
          onKeyDown?.(event);
        }, 100);
        return;
      }

      if (event.key === "Escape") {
        input.blur();
      }

      onKeyDown?.(event);
    },
    [onKeyDown],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        handlePopoverOpen(isOpen);
        if (!isOpen) {
          onBlur?.();
        }
      }}
      modal={modal}
    >
      <PopoverTrigger
        asChild
        onClick={(e) => {
          if (readOnly) e.preventDefault();
        }}
      >
        <Button
          ref={ref}
          variant={variant}
          role="combobox"
          aria-expanded={open}
          data-combobox-id={comboBoxId}
          className={cn(
            "flex justify-between w-full overflow-hidden p-2 h-10 gap-1",
            !value && "text-muted-foreground",
            value && highlightWhenSelected && "bg-secondary",
            open &&
              "outline outline-2 outline-muted-foreground border-transparent outline-offset-[-2px]",
            className,
          )}
          disabled={disabled}
          readOnly={readOnly}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {children ? (
            children({ value, placeholder, readOnly, disabled })
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex flex-row gap-2">
                <span className="text-ellipsis overflow-hidden text-left">
                  {value ? (
                    value.label
                  ) : (
                    <div className="flex flex-row gap-1.5 items-center">
                      {props.icon && <props.icon className="h-4 w-4" />}
                      {placeholder}
                    </div>
                  )}
                </span>
              </div>
              <div className="flex w-full text-gray-500 text-xs overflow-hidden">
                <span className="flex items-center gap-2">
                  {value?.subLabel ?? null}
                  {specialNotice && <span>{specialNotice}</span>}
                </span>
              </div>
            </div>
          )}
          {!readOnly && (
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          )}
          {value && enableClear && !readOnly && !disabled && (
            <div>
              <X
                className="w-4 h-4 opacity-50 hover:opacity-100 cursor-pointer"
                onClick={handleClear}
              />
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("p-0 z-[9999] w-96")}
        sideOffset={sideOffset}
        side={side}
        align={align}
        container={container}
      >
        <Command filter={filterBySearch}>
          <CommandInput
            ref={inputRef}
            data-combobox-id={comboBoxId}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onValueChange={(value) => {
              setInputValue(value);
            }}
            placeholder={t("search")}
            onFocus={onFocus}
            onKeyDown={handleInputKeyDown}
          />
          <CommandList>
            <CommandEmpty className="py-2">
              {createNew ? (
                <CreateNewOption
                  label={createNew.label}
                  searchTerm={inputValue}
                  onCreateNew={createNew.onCreateNew}
                />
              ) : typeof emptyLabel === "function" ? (
                (emptyLabel as (props: { input: string }) => React.ReactNode)({
                  input: inputValue,
                })
              ) : typeof emptyLabel === "string" ? (
                <div className="flex items-center justify-center p-2 text-sm text-muted-foreground">
                  {emptyLabel}
                </div>
              ) : (
                emptyLabel
              )}
            </CommandEmpty>
            <CommandItem value="-" className="hidden" />
            <CommandGroup
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  className="text-ellipsis group"
                  onSelect={(value) => {
                    onSelect(value);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      currentOption?.value === item.value
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <div className="w-full flex flex-row gap-1">
                    <div className="flex flex-row gap-1">
                      <span className="text-muted-foreground">
                        {item.subLabel}
                      </span>
                      {item.subLabel && (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <span>{item.label}</span>
                    </div>
                  </div>
                  <div className="ml-auto pl-1 opacity-0 group-aria-selected:opacity-100 transition-opacity">
                    <div className="flex items-center gap-1 bg-border rounded px-1.5 py-1">
                      <span className="text-[10px] capitalize leading-none">
                        {t("enter")}
                      </span>
                      <Shortcut.ShortcutEnter
                        size="sm"
                        className="bg-transparent p-0"
                      />
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export { ComboBox };
